import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProcessSection = ({ onGetStarted }) => {
  const steps = [
    {
      number: '01',
      icon: 'Upload',
      title: 'Upload Details',
      description: 'Take photos and share basic information about your vehicle. Our smart form makes it quick and easy.',
      details: ['Vehicle photos', 'Basic information', 'Condition assessment'],
      time: '5 minutes'
    },
    {
      number: '02', 
      icon: 'FileText',
      title: 'Get Offer',
      description: 'Receive multiple competitive offers from verified dealers nationwide within 24 hours.',
      details: ['Multiple offers', 'Competitive pricing', 'No obligation'],
      time: '24 hours'
    },
    {
      number: '03',
      icon: 'Calendar',
      title: 'Schedule Pickup',
      description: 'Choose your preferred time and location. We handle all paperwork and inspection at your convenience.',
      details: ['Flexible scheduling', 'Free inspection', 'Paperwork handled'],
      time: '30 minutes'
    },
    {
      number: '04',
      icon: 'DollarSign', 
      title: 'Get Paid',
      description: 'Receive instant payment via bank transfer, certified check, or cash. No waiting, no hassle.',
      details: ['Instant payment', 'Multiple options', 'Secure transaction'],
      time: 'Instant'
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            How It Works
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Our streamlined process makes selling your car as easy as 1-2-3-4. 
            From upload to payment in as little as 24 hours.
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Desktop Process Line */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-blue-400 to-green-400"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {/* Mobile Process Line */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden absolute left-6 top-16 w-0.5 h-24 bg-gradient-to-b from-blue-400 to-blue-200"></div>
                )}

                <div className="relative bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-border hover:border-blue-200 hover:shadow-lg transition-all duration-300 group">
                  {/* Step Number & Icon */}
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="relative">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <Icon name={step.icon} size={20} color="white" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-white border-2 border-blue-500 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">
                        {step.number}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground group-hover:text-blue-600 transition-colors">
                        {step.title}
                      </h3>
                      <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                        <Icon name="Clock" size={12} />
                        <span>{step.time}</span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Details List */}
                  <ul className="space-y-1 mb-4">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-center space-x-2 text-xs text-muted-foreground">
                        <Icon name="Check" size={12} className="text-green-500 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Progress Indicator */}
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-green-500 h-1 rounded-full transition-all duration-1000"
                      style={{ width: `${((index + 1) / steps.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Ready to Start Selling?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Join thousands of satisfied customers who've experienced our hassle-free selling process. 
              Get your offer today!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={onGetStarted}
                iconName="ArrowRight" 
                iconPosition="right"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                Start Selling Now
              </Button>
              <Button size="lg" variant="outline">
                <Icon name="Phone" size={16} className="mr-2" />
                Call (555) 123-4567
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;